import { ChatRoomDisplayContext } from '@/contexts/ChatRoomDisplayContext';
import { CommonConfigContext } from '@/contexts/ConfigContext';
import { FullScreenContext } from '@/contexts/FullscreenContext';
import { GlobalDataContext } from '@/contexts/GlobalDataContext';
import { LiteUIContext } from '@/contexts/LiteUIContext';
import { ScreenContext } from '@/contexts/ScreenContext';
import { WebSocketContext } from '@/contexts/WebSocketContext';
import { getLocaleByCode, Locale, LocaleCode } from '@/models/Locale';
import { POPUP_TYPE } from '@/models/Popup';
import { ROUTE_GAME } from '@/models/Route';
import { Voice } from '@/models/Voice';
import { GameType as GameTypeEnum } from '@/models/games/enums/GameType';
import { getHostById } from '@/modules/host/slice';
import {
    getLandingState,
    getMainChatState,
    getMainGiftState,
    getMainUserState,
    getPromotionToolState,
} from '@/modules/main/selector';
import { promotionToolSliceActions } from '@/modules/main/slice/promotiontool/promotionTool';
import {
    PromotionEventType,
    PromotionPage,
} from '@/modules/popup/promotiontool/components/PromotionDialog';
import { popupSliceActions } from '@/modules/popup/slice';
import { routeSliceActions } from '@/modules/routeController/slice';
import { getSettingSoundState } from '@/modules/setting/selector';
import { RootState } from '@/store/store';
import { gotoOldUI, gotoOldWeb } from '@/utils/commonFunc';
import {
    useContext,
    useEffect,
    useLayoutEffect,
    useMemo,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { batch, useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { usePromotionTool } from './popup/promotiontool/usePromotionTool';
import { BetViewMode } from './useGlobalData';
import { useIsLobby } from './useIsLobby';

const reference = {
    list: {
        icoSizePortrait: 36 * 1.25,
        icoSizeLandscape: 44 * 1.5,
        stepIndexPortrait: 2,
        stepIndexLandscape: 3,
    },
    switch: {
        icoSizePortrait: 32 * 1.25,
        icoSizeLandscape: 48 * 1.5,
        tooltipSizePortrait: 23,
        tooltipSizeLandscape: 38,
        // tooltipSizePortrait: 28,
        // tooltipSizeLandscape: 48,
        stepIndexPortrait: 2,
        stepIndexLandscape: 3,
    },
    tooltip: {
        stepIndexPortrait: 0,
        stepIndexLandscape: 2,
    },
    betview: {
        icoSizePortrait: 86,
        icoSizeLandscape: 116,
        checkBoxSizePortrait: 26.3,
        checkBoxSizeLandscape: 31.9,
    },
    logout: {
        icoSizePortrait: 38,
        icoSizeLandscape: 60,
        stepIndexPortrait: 0,
        stepIndexLandscape: 2,
    },
};

enum ItemList {
    Leaderboard,
    ExploreRewards,
    BetRecord,
    GiftRecord,
    MemberReport,
    GameRules,
    Language,
    Sound,
    Agreement,
    Feedback,
    ChangePassword,
}
const data = {
    list: {
        [ItemList.Leaderboard]: 'promotion_tool.leaderboard',
        [ItemList.ExploreRewards]: 'promotion_tool.explore_rewards',
        [ItemList.BetRecord]: 'menu.bet_record',
        [ItemList.GiftRecord]: 'menu.gift_record',
        [ItemList.GameRules]: 'menu.game_rules',
        [ItemList.Language]: 'menu.language',
        [ItemList.Sound]: 'menu.sound',
        // [ItemList.Agreement]:'menu.agreement',
        [ItemList.Feedback]: 'menu.feedback',
        // [ItemList.ChangePassword]:'menu.change_password'
    },
    logout: 'menu.logout',
};

export type useSettingMenuState = ReturnType<typeof useSettingMenu>;
export const useSettingMenu = () => {
    const { i18n } = useTranslation();
    const location = useLocation();
    const [hostId, setHostId] = useState(NaN);
    const dispatch = useDispatch();
    const { isLandscape, scaleFactor } = useContext(ScreenContext);

    const { GameType } = useSelector((state: RootState) =>
        getHostById(state, hostId)
    );
    const { canBack, canBackWeb } = useSelector(getLandingState);
    const { disableLogoutLobby, hideFeedback } =
        useContext(CommonConfigContext);
    // const { LobbyType:lobby } = useSelector((state: RootState) => getMainUserState(state));
    const { IsTrial, LobbyCode: serLobbyCode } = useSelector(getMainUserState);
    const isShowEvent = useSelector(
        (state: RootState) => getPromotionToolState(state).Id > 0
    );
    const isOpenPromotionDialog = useSelector((state: RootState) =>
        getPromotionToolState(state)
    ).IsOpenDialog;
    const eventType = useSelector((state: RootState) =>
        getPromotionToolState(state)
    ).EventType;
    const { eventConcluded } = usePromotionTool();

    const isLobby = useIsLobby();
    const { changeClientUI } = useContext(WebSocketContext);

    const { isLite, setIsLite } = useContext(LiteUIContext);
    const [switchLite, setSwitchLite] = useState(isLite);
    const { betView, setBetView } = useContext(GlobalDataContext);
    const { lobbyChatEnable } = useSelector(getMainChatState);

    const { hideFullScreen, onlyLangList, langOnly, langExclude } =
        useContext(CommonConfigContext);
    const { isFullScreen, toggleFullScreen } = useContext(FullScreenContext);
    const { lobbyGiftEnable, currencyGiftEnable } =
        useSelector(getMainGiftState);
    const {
        isChatPanelEnabled,
        setIsChatPanelEnabled,
        showOtherPlayerGift,
        setShowOtherPlayerGift,
    } = useContext(ChatRoomDisplayContext);

    const [drawerOpen, setDrawerOpen] = useState(false);

    const [tooltipLiteModeOpen, setTooltipLiteModeOpen] = useState(false);
    const [tooltipBetAreaPositionOpen, setTooltipBetAreaPositionOpen] =
        useState(false);

    const [isGiftRecordOpen, setIsGiftRecordOpen] = useState(false);
    const [isBetRecordOpen, setIsBetRecordOpen] = useState(false);
    const [isGameRulesOpen, setIsGameRulesOpen] = useState(false);
    const [isLanguageOpen, setIsLanguageOpen] = useState(false);
    const [isSoundOpen, setIsSoundOpen] = useState(false);
    const [isAgreementOpen, setIsAgreementOpen] = useState(false);
    const [isFeedBackOpen, setIsFeedBackOpen] = useState(false);
    const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
    const [showBack, setShowBack] = useState(false);
    const [showBackWeb, setShowBackWeb] = useState(false);
    const [switchZoom, setSwitchZoom] = useState(1);
    const [switchBetView, setSwitchBetView] = useState(betView);

    const [locales, setLocales] = useState<Array<LocaleCode>>([]);
    const [language, setLanguage] = useState<string>(Locale.en_US.long);
    const [voiceSound, setVoiceSound] = useState<Voice>(Voice.English1);
    const { Voice2: currentVoice } = useSelector(getSettingSoundState);

    const closeDrawer = (currentSwitchBetView?: BetViewMode) => {
        const _switchBetView = currentSwitchBetView ?? switchBetView;
        if (betView != _switchBetView) {
            setBetView(_switchBetView);
        }
        if (isLite != switchLite) {
            setIsLite(switchLite);
            // dispatch(routeSliceActions.goto(ROUTE_LOBBY));
            dispatch(routeSliceActions.refreshTo(location.pathname));
        }

        setTooltipLiteModeOpen(false);
        setTooltipBetAreaPositionOpen(false);
        setDrawerOpen(false);
    };

    const logout = () => {
        dispatch(
            popupSliceActions.open(
                'system.logout_confirm',
                POPUP_TYPE.LOGOUT_CONFIRM
            )
        );
    };

    const onClickSetting = () => {
        setDrawerOpen(true);
    };

    const onClickMenuItem = (key: string) => {
        switch (key) {
            case 'promotion_tool.leaderboard':
                if (!eventConcluded()) {
                    batch(() => {
                        dispatch(
                            promotionToolSliceActions.updatePage(
                                PromotionPage.Ranking
                            )
                        );
                        dispatch(promotionToolSliceActions.isOpenDialog(true));
                    });
                }
                break;
            case 'promotion_tool.explore_rewards':
                if (eventConcluded()) {
                    dispatch(promotionToolSliceActions.isOpenDialog(false));
                    dispatch(
                        popupSliceActions.open(
                            'promotion_tool.popup_event_ended'
                        )
                    );
                    return;
                }

                batch(() => {
                    dispatch(
                        promotionToolSliceActions.updatePage(
                            PromotionPage.Gacha
                        )
                    );
                    dispatch(promotionToolSliceActions.isOpenDialog(true));
                });
                break;
            case 'menu.bet_record':
                setIsBetRecordOpen(true);
                break;
            case 'menu.gift_record':
                setIsGiftRecordOpen(true);
                break;
            case 'menu.game_rules':
                setIsGameRulesOpen(true);
                break;
            case 'menu.language':
                setIsLanguageOpen(true);
                break;
            case 'menu.sound':
                setIsSoundOpen(true);
                break;
            case 'menu.agreement':
                setIsAgreementOpen(true);
                break;
            case 'menu.feedback':
                setIsFeedBackOpen(true);
                break;
            case 'menu.change_password':
                setIsChangePasswordOpen(true);
                break;
        }
    };

    const onClickBackToOldClient = () => {
        changeClientUI();
        if (showBack) {
            gotoOldUI();
        } else if (showBackWeb) {
            gotoOldWeb();
        }
    };

    const enableBetView = useMemo(() => {
        const url = window.location.href;
        if (url.includes(ROUTE_GAME)) {
            switch (GameType) {
                case GameTypeEnum.Blackjack2:
                case GameTypeEnum.Blackjack:
                case GameTypeEnum.UltraRoulette:
                case GameTypeEnum.Roulette:
                case GameTypeEnum.SicBo:
                case GameTypeEnum.ThaiHiLo:
                    // case GameTypeEnum.FishPrawnCrab:
                    return false;
                default:
                    return true;
            }
        } else {
            return false;
        }
    }, [GameType]);

    const originBetViewStyle = useMemo(() => {
        if (!isLandscape && enableBetView) {
            if (switchBetView === BetViewMode.Original) {
                return 'bet-view-button-selected';
            } else {
                return 'bet-view-button';
            }
        } else {
            return 'bet-view-button';
        }
    }, [enableBetView, isLandscape, switchBetView]);

    const reverseBetViewStyle = useMemo(() => {
        if (!isLandscape && enableBetView) {
            if (switchBetView === BetViewMode.Reverse) {
                return 'bet-view-button-selected';
            } else {
                return 'bet-view-button';
            }
        } else {
            return 'bet-view-button';
        }
    }, [switchBetView, enableBetView, isLandscape]);

    const isLeaderboard = useMemo(() => {
        return eventType === PromotionEventType.Leaderboard;
    }, [eventType]);

    //initialization
    useEffect(() => {
        if (hideFeedback) {
            data.list[ItemList.Feedback] = '';
        }

        // if(lobby != LobbyType.Credit){
        //     data.list[ItemList.ChangePassword] = '';
        // }

        if (disableLogoutLobby) data.logout = '';
    }, []);
    useEffect(() => {
        if (onlyLangList) {
            const dl = i18n.language;
            setLanguage(dl);
            setVoiceSound(currentVoice);

            if (onlyLangList && onlyLangList.length > 0) {
                const l = Object.values(Locale).filter(l => {
                    const shortLang = l.short;
                    if (onlyLangList.indexOf(shortLang) >= 0) return true;
                    return false;
                });
                setLocales(l);
            } else {
                const l = Object.values(Locale).filter(l => {
                    const shortLang = l.short;
                    if (
                        langExclude[shortLang]?.lobby?.includes(serLobbyCode) ||
                        langExclude[shortLang]?.lobby?.includes('all')
                    )
                        return false;
                    if (langOnly[l.short]) {
                        if (langOnly[l.short]?.includes(serLobbyCode)) {
                            return true;
                        }
                        return false;
                    }
                    return true;
                });
                setLocales(l);
            }
        }
    }, [onlyLangList]);
    useEffect(() => {
        if (onlyLangList) {
            const tmp = locales.find(v => v.long == i18n.language);
            if (!tmp) {
                //if not support
                languageChangeHandler('en-US');
            }
        }
    }, [locales, i18n.language]);

    const languageChangeHandler = (lang: string) => {
        const defaultVoice = getLocaleByCode('long', lang).voice;
        setLanguage(lang);
        i18n.changeLanguage(lang);
        setVoiceSound(defaultVoice);
    };
    //Update data list item by server data
    useEffect(() => {
        if (isShowEvent) {
            if (isLeaderboard) {
                data.list[ItemList.Leaderboard] = 'promotion_tool.leaderboard';
                data.list[ItemList.ExploreRewards] = '';
            } else {
                data.list[ItemList.Leaderboard] = '';
                data.list[ItemList.ExploreRewards] =
                    'promotion_tool.explore_rewards';
            }
        } else {
            data.list[ItemList.Leaderboard] = data.list[
                ItemList.ExploreRewards
            ] = '';
        }
    }, [isShowEvent, isLeaderboard]);

    useEffect(() => {
        data.list[ItemList.GiftRecord] =
            lobbyGiftEnable && currencyGiftEnable && !IsTrial
                ? 'menu.gift_record'
                : '';
    }, [lobbyGiftEnable, currencyGiftEnable]);

    useEffect(() => {
        setSwitchZoom(scaleFactor * (isLandscape ? 3 : 2));
    }, [scaleFactor]);

    useEffect(() => {
        const url = window.location.href;
        if (url.includes(ROUTE_GAME)) {
            switch (GameType) {
                case GameTypeEnum.AndarBahar:
                case GameTypeEnum.Baccarat:
                case GameTypeEnum.Blackjack2:
                case GameTypeEnum.Blackjack:
                case GameTypeEnum.Dragon:
                case GameTypeEnum.PokDeng:
                case GameTypeEnum.Roulette:
                case GameTypeEnum.SeDie:
                case GameTypeEnum.SicBo:
                case GameTypeEnum.TeenPatti2020:
                    setShowBack(canBack);
                    setShowBackWeb(canBackWeb);
                    break;
                default:
                    setShowBack(false);
                    setShowBackWeb(false);
                    break;
            }
        } else {
            setShowBack(canBack);
            setShowBackWeb(canBackWeb);
        }
    }, [GameType, canBack, canBackWeb, isLobby]);

    useEffect(() => {
        if (isOpenPromotionDialog) {
            closeDrawer();
        }
    }, [isOpenPromotionDialog]);

    useLayoutEffect(() => {
        closeDrawer();
    }, [switchLite]);

    return {
        showOtherPlayerGift,
        setShowOtherPlayerGift,
        lobbyGiftEnable,
        isChatPanelEnabled,
        setIsChatPanelEnabled,
        lobbyChatEnable,
        IsTrial,
        setHostId,
        reference,
        data,
        isLite,
        setSwitchLite,
        switchLite,
        hideFullScreen,
        isFullScreen,
        toggleFullScreen,
        drawerOpen,
        setDrawerOpen,
        closeDrawer,
        tooltipLiteModeOpen,
        setTooltipLiteModeOpen,
        tooltipBetAreaPositionOpen,
        setTooltipBetAreaPositionOpen,
        isGiftRecordOpen,
        setIsGiftRecordOpen,
        isBetRecordOpen,
        setIsBetRecordOpen,
        isGameRulesOpen,
        setIsGameRulesOpen,
        isLanguageOpen,
        setIsLanguageOpen,
        isSoundOpen,
        setIsSoundOpen,
        isAgreementOpen,
        setIsAgreementOpen,
        isFeedBackOpen,
        setIsFeedBackOpen,
        isChangePasswordOpen,
        setIsChangePasswordOpen,
        showBack,
        showBackWeb,
        switchZoom,
        setSwitchBetView,
        enableBetView,
        originBetViewStyle,
        reverseBetViewStyle,
        onClickSetting,
        onClickMenuItem,
        onClickBackToOldClient,
        logout,
        locales,
        voiceSound,
        setVoiceSound,
        language,
        languageChangeHandler,
    };
};
